class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.syncEmptyState();

    this.stateObserver = new MutationObserver(() => this.syncEmptyState());
    this.stateObserver.observe(this, { childList: true, subtree: true });
  }

  syncEmptyState() {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;

    const merchandiseCount = Number(inner.dataset.cartMerchandiseCount || 0);
    const isEmpty = merchandiseCount === 0;
    this.classList.toggle('is-empty', isEmpty);

    const items = this.querySelector('cart-drawer-items');
    if (items) items.classList.toggle('is-empty', isEmpty);
    this.syncWallets();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.syncEmptyState();
    if (triggeredBy) {
      this.classList.remove('is-added-notice');
      this.setActiveElement(triggeredBy);
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.syncWallets();
      const notice = this.classList.contains('is-added-notice') ? this.querySelector('dyefor-cart-added') : null;
      const container = notice || (this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : this.querySelector('.drawer__inner'));
      trapFocus(container, notice?.querySelector('[data-added-close]') || container);
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active', 'is-added-notice');
    this.syncWallets();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  syncWallets() {
    const template = this.querySelector('[data-cart-wallet-template]');
    const notice = this.querySelector('dyefor-cart-added');
    const addedMode = this.classList.contains('is-added-notice');
    const target = this.classList.contains('active') && (!addedMode || notice?.dataset.pairComplete === 'true')
      ? this.querySelector(addedMode ? '[data-added-wallets]' : '[data-cart-wallets]') : null;
    for (const slot of this.querySelectorAll('[data-added-wallets], [data-cart-wallets]')) {
      if (slot !== target && slot.firstChild) slot.replaceChildren();
    }
    // Instantiate the untouched native markup only in the visible cart view.
    if (target && !target.firstChild && template) target.append(template.content.cloneNode(true));
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const wasActive = this.classList.contains('active');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));
    this.syncEmptyState();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      let showNotice = false;
      try { showNotice = this.querySelector('dyefor-cart-added')?.prepare(parsedState) || false; }
      catch (error) { console.warn('Cart notification unavailable; showing full cart.', error); }
      this.classList.toggle('is-added-notice', showNotice);
      const addedItems = typeof parsedState.item_count === 'number' ? [] : (parsedState.items || [parsedState]);
      const protectionOnly = addedItems.length > 0 && addedItems.every(item => item.handle === 'dyefor-shipping-protection');
      if (protectionOnly && !wasActive) return;
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
